// Skips `localOnly` messages (e.g. presence-rejoin notifications), which carry a client-clock
// timetoken that is not a valid PubNub history cursor.
export const lastServerTimetoken = (conversation?: Models.Chat): string | undefined => {
    const serverMessages = (conversation?.messages ?? []).filter(message => !message.localOnly)
    return serverMessages[serverMessages.length - 1]?.timetoken
}

// Classifies a presence actor as the patient the same way the still-live `Presence` PubNub
// Function does (`Presence.js:73`, `message.uuid === channel.split('.')[2]`), rather than from
// `chat.meta.pubnub_user_id` - a field the F1 admission path no longer writes (S5 X1). Parity with
// the Function's own classification is the standard this needs to hold, not identity-correctness.
export const isPatientUuid = (channelName: string, uuid: string): boolean => {
    return channelName.split('.')[2] === uuid
}

const LEFT_CHAT_TYPES: Models.MessageType[] = ['system:patient-left-chat', 'system:chatter-left-chat']

// True for the wire copy of a left-chat notice that the still-live `Presence` Function publishes
// (`Presence.js:60-79`), which S5 renders locally instead of via the wire message. Keyed on
// `!localOnly` rather than `type` alone, because the locally-derived notice carries the *same*
// type - a type-only filter would remove the thing S5 just derived (S5 X2). Stays true, and the
// filter that reads it stays at the render layer, until the `Presence` Function is deleted and
// history has aged past its retention window (S5 X4) - see `DELETION-ORDER.md`.
export const isWirePublishedLeftChatNotice = (message: Models.Message): boolean => {
    return LEFT_CHAT_TYPES.includes(message.type) && !message.localOnly
}

// `025-` H2: dropping every wire-published left-chat notice unconditionally also drops it when no
// locally-derived copy exists for that same occurrence - after a reload (the local copy is
// Redux-only and does not survive one) or for a specialist who opens the chat after the patient
// already left (they never received the live presence event `checkIfPatientOrChatterLeftChat`
// derives it from). The wire copy is published with `storeInHistory: true` (`Presence.js:63`), so
// it is the only durable record of the event - dropping it unconditionally loses the notice
// entirely instead of just avoiding a same-session double-render.
//
// There is no shared identifier between the two copies (the local copy mints its own 32-char id
// and a client-clock timetoken; the wire copy has its own 36-char id and PubNub's timetoken), so
// pairing is by occurrence count per `type` + `sender_id`: only drop a wire notice while a local
// notice for that same actor/type combination is still unaccounted for. This correctly handles a
// patient leaving and rejoining more than once in the same session, where a naive "any local
// notice of this type exists" check would wrongly drop every wire duplicate after the first.
export const filterDuplicateWireLeftChatNotices = (messages: Models.Message[]): Models.Message[] => {
    const remainingLocalNoticesByKey: { [key: string]: number } = {}

    messages.forEach(message => {
        if (LEFT_CHAT_TYPES.includes(message.type) && message.localOnly) {
            const key = `${message.type}:${message.sender_id}`
            remainingLocalNoticesByKey[key] = (remainingLocalNoticesByKey[key] || 0) + 1
        }
    })

    return messages.filter(message => {
        if (!isWirePublishedLeftChatNotice(message)) {
            return true
        }

        const key = `${message.type}:${message.sender_id}`
        if (remainingLocalNoticesByKey[key] > 0) {
            remainingLocalNoticesByKey[key] -= 1
            return false
        }

        return true
    })
}

const GOOGLE_ANALYTICS_ID_PARAM = 'google_analytics_id='
const GOOGLE_TAG_MANAGER_ID_PARAM = 'google_tag_manager_id='

export const filterOutGoogleQueryParams = (text: string) => {
    let textToReturn = text || ''

    if (textToReturn.includes(`?${GOOGLE_ANALYTICS_ID_PARAM}`)) {
        textToReturn.replace(`?${GOOGLE_ANALYTICS_ID_PARAM}`, `&${GOOGLE_ANALYTICS_ID_PARAM}`)
    }

    if (textToReturn.includes(`?${GOOGLE_TAG_MANAGER_ID_PARAM}`)) {
        textToReturn.replace(`?${GOOGLE_TAG_MANAGER_ID_PARAM}`, `&${GOOGLE_TAG_MANAGER_ID_PARAM}`)
    }

    if (
        textToReturn.includes(`&${GOOGLE_ANALYTICS_ID_PARAM}`) ||
        textToReturn.includes(`&${GOOGLE_TAG_MANAGER_ID_PARAM}`)
    ) {
        const textToReturnArray = textToReturn
            .split('&')
            .map(param => {
                if (
                    param.includes(`${GOOGLE_ANALYTICS_ID_PARAM}`) ||
                    param.includes(`${GOOGLE_TAG_MANAGER_ID_PARAM}`)
                ) {
                    return ''
                } else {
                    return param
                }
            })
            .filter(p => p !== '')

        if (textToReturnArray.length > 1) {
            const firstPart = `${textToReturnArray[0]}?${textToReturnArray[1]}`
            const secondPart = textToReturnArray.slice(2).join('&')
            textToReturn = [firstPart, secondPart].join('&')
        } else {
            textToReturn = textToReturnArray[0]
        }
    }
    return textToReturn
}
